import { CommonModule } from '@angular/common';
import { Component, effect, inject, model, signal, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'app/core/services/auth/auth.service';
import { LanguageService, SupportedLang } from 'app/core/services/language/language.service';
import { LANGUAGE_OPTIONS } from 'app/core/constants/language-options.constant';
import { IUserMenuOption } from 'app/shared/interfaces/IMainLayout';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';

type UserMenuView = 'main' | 'language';

@Component({
  selector: 'user-menu',
  standalone: true,
  imports: [CommonModule, PopoverModule, ButtonModule, TranslateModule],
  templateUrl: './user-menu.component.html',
  styleUrl: './user-menu.component.css'
})
export class UserMenuComponent {
  readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);
  visible = model<boolean>(false);
  private anchorEvent = signal<MouseEvent | null>(null);
  // Elemento de anclaje del popover (el botón), capturado en el click mientras
  // event.currentTarget es válido; el effect muestra el popover asíncronamente.
  private anchorEl: HTMLElement | null = null;

  // Vista activa del popover: lista principal o submenú de idioma.
  readonly view = signal<UserMenuView>('main');

  // Idioma activo (signal del LanguageService, T1) para marcar el ✓.
  readonly activeLang = this.languageService.activeLang;

  // Opciones compartidas (constante); el nombre del idioma se traduce al idioma
  // activo en el template (no es endónimo): es → Español/Inglés, en → Spanish/English.
  readonly languageOptions = LANGUAGE_OPTIONS;

  @ViewChild('op') op!: Popover;

  constructor (){
    effect(() => {
      const isOpen = this.visible();
      const ev = this.anchorEvent();

      if (!this.op) return;

      if (isOpen) {
        // Elemento estable como target (no depender de currentTarget/target del evento).
        if (ev && this.anchorEl) this.op.show(ev, this.anchorEl);
      } else {
        this.op.hide();
      }
    });
  }

  options: IUserMenuOption[] = [
    /*
    {
      id: "1",
      name: 'Mi perfil',
      image: 'profile.svg',
      action: () => console.log("Ir al perfil")
    },
    {
      id: "2",
      name: 'Abrir Suite',
      image: 'suite.svg',
      action: () => console.log("Ir a la suite")
    },
    */
    {
      id: "language",
      name: 'common.languageMenu.title',
      image: 'translate.svg',
      // Abre el submenú de idioma en lugar de ejecutar una acción terminal.
      action: () => this.view.set('language')
    },
    {
      id: "3",
      name: 'common.actions.logout',
      image: 'log-out.svg',
      // logoutEverywhere(): bajo el realm cierra también la sesión del IdP; en el camino
      // legado es exactamente el logout() de siempre (Task 11d).
      action: () => this.authService.logoutEverywhere()
    },
  ];

  toggle(event: any) {
      this.op.toggle(event);
  }

  selectOption(option: IUserMenuOption) {
    // El item de idioma navega al submenú; el popover permanece abierto.
    if (option.id === 'language') {
      option.action();
      return;
    }
    this.op.hide();
    option.action();
  }

  // Cambio de idioma en vivo (sin recarga) vía LanguageService (T1).
  selectLanguage(lang: SupportedLang) {
    this.languageService.setLanguage(lang);
  }

  backToMain() {
    this.view.set('main');
  }

  anchor(ev: MouseEvent) {
    this.anchorEvent.set(ev);
    // Captura el botón AHORA (síncrono), antes de que currentTarget se anule.
    this.anchorEl = ev.currentTarget as HTMLElement;
  }

  onPopoverHide() {
    this.visible.set(false);
    // Reinicia a la lista principal para la próxima apertura.
    this.view.set('main');
  }
}
